import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { selectService, setNodeDetails } from "./index";
import { selectNodes } from "../Applications";
import { infoIcon, downIcon } from "../../assets/icons";
import { setSelectedModal } from "../../components/organisms/Modal";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { EMPTY_ARRAY, EMPTY_STRING } from "../../constants";
import { SearchBar } from "../../components/molecules";

const NODE_DATA_TRANSFER_KEY = "application/reactflow";
const { NODE_DESCRIPTION_MODAL } = MODALS;
const MOVE = "move";

const initialAccordionState = {
  start: false,
  database: false,
  logical: false,
  object: false,
  response: false,
};

const ServiceLeftSidebar = ({ draggable, onDoubleClick }) => {
  const dispatch = useDispatch();
  const selectedServiceNodes = useSelector(selectNodes);
  const selectedService = useSelector(selectService);
  const [serviceNodes, setServiceNodes] = useState(EMPTY_ARRAY);
  const [accordionState, setAccordionState] = useState(initialAccordionState);
  const [filteredNodes, setFilteredNodes] = useState(EMPTY_ARRAY);
  const [searchedKeyword, setSearchedKeyword] = useState(EMPTY_STRING);

  useEffect(() => {
    if (selectedServiceNodes) {
      const filteredServiceNodes = selectedServiceNodes.filter(
        (nodeData) => nodeData.nodes.length
      );
      setServiceNodes(filteredServiceNodes);
    }
  }, [dispatch, selectedServiceNodes]);

  const onDragStart = (event, node) => {
    event.dataTransfer.setData(NODE_DATA_TRANSFER_KEY, JSON.stringify(node));
    event.dataTransfer.effectAllowed = MOVE;
  };

  const handleAccordion = useCallback(
    (nodeName) => {
      setAccordionState((prevState) => ({
        ...accordionState,
        [nodeName]: !prevState[nodeName],
      }));

      const selectedNodeGroup = document.getElementById(nodeName);
      if (selectedNodeGroup.style.maxHeight)
        selectedNodeGroup.style.maxHeight = null;
      else
        selectedNodeGroup.style.maxHeight =
          selectedNodeGroup.scrollHeight + "px";
    },
    [accordionState]
  );

  const handleOnClick = useCallback(
    (node) => {
      dispatch(setNodeDetails(node));
      dispatch(setSelectedModal(NODE_DESCRIPTION_MODAL));
    },
    [dispatch]
  );

  const filterNodes = useCallback(
    (each) =>
      each.card.enabled &&
      ((selectedService?.serviceType === "endpoint" &&
        !each.card.hideForEndpoint) ||
        (selectedService?.serviceType === "backgroundTask" &&
          !each.card.hideForTasks) || 
          (selectedService?.serviceType === "subflow" &&
          !each.card.hideForEndpoint)),
    [selectedService]
  );

  const handleOnSearch = useCallback(
    (value) => {
      setAccordionState(initialAccordionState);
      if (!value) {
        setFilteredNodes(EMPTY_ARRAY);
        setSearchedKeyword(EMPTY_STRING);
        return;
      }
      const searchedNodesResult = serviceNodes.reduce(
        (searchedNode, { enabled, nodes }) => {
          if (!enabled) return searchedNode;
          const searchResult = nodes.filter(({ name }) =>
            name.toLowerCase().includes(value.toLowerCase())
          );
          return [...searchedNode, ...searchResult];
        },
        []
      );
      setSearchedKeyword(value);
      setFilteredNodes(searchedNodesResult);
    },
    [serviceNodes]
  );

  return (
    <>
      {serviceNodes.length ? (
        <>
          <div className="text-center bg-gray-900 text-white">Node Library</div>
          <div className="bg-gray-900 p-1">
            <SearchBar
              placeholder="Search"
              draggable={draggable}
              handleOnSearch={handleOnSearch}
            />
          </div>
          {searchedKeyword ? (
            filteredNodes.length ? (
              <>
                {filteredNodes.map((childNode) => (
                  <div
                    key={childNode.name}
                    className={
                      "flex items-center justify-between mb-1 hover:bg-blue-300 p-2 rounded whitespace-normal text-white cursor-pointer"
                    }
                    onDragStart={(event) => onDragStart(event, childNode)}
                    onDoubleClick={() => onDoubleClick(childNode)}
                    draggable
                  >
                    <div>{childNode.name}</div>
                    <div className="flex items-center">
                      {childNode.card.isParent && (
                        <div className="text-xs	italic">(parent)</div>
                      )}
                      <button
                        className={classNames(
                          "w-7 h-7 rounded-full ml-auto flex items-center justify-center",
                          { "cursor-col-resize": draggable }
                        )}
                        onClick={() => handleOnClick(childNode)}
                      >
                        <img
                          src={infoIcon}
                          className="w-4 h-4 bg-blue-600 rounded-xl hover:shadow-[0_0_0_8px_rgb(59,130,246)]"
                          alt="info-icon"
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-center text-2xl mt-48 text-slate-300">
                No Nodes Found !
              </div>
            )
          ) : (
            serviceNodes
              .filter((nodeGroup) => nodeGroup.enabled)
              .map((node) => (
                <div key={node.name} className="overflow-hidden">
                  <button
                    className={classNames(
                      "bg-gray-900 text-white p-3 w-full text-md flex duration-500 hover:bg-slate-300 justify-between",
                      { "cursor-col-resize": draggable }
                    )}
                    onClick={() => handleAccordion(node.name)}
                  >
                    {node.name}
                    <img
                      src={downIcon}
                      alt="down-icon"
                      className={classNames("w-6", {
                        "rotate-180": accordionState[node.name],
                      })}
                    />
                  </button>
                  <div
                    id={node.name}
                    className="px-2.5 truncate ease-out duration-300 max-h-0"
                  >
                    <div className="p-2.5 w-auto">
                      {node.nodes.filter(filterNodes).map((childNode) => (
                        <div
                          key={childNode.nodeId}
                          className={
                            "flex items-center justify-between mb-1 hover:bg-blue-300 p-2 rounded whitespace-normal text-white cursor-pointer"
                          }
                          onDragStart={(event) => onDragStart(event, childNode)}
                          onDoubleClick={() => onDoubleClick(childNode)}
                          draggable
                        >
                          <div>{childNode.name}</div>
                          <div>
                            <button
                              className={classNames(
                                "w-7 h-7 rounded-full ml-auto flex items-center justify-center",
                                { "cursor-col-resize": draggable }
                              )}
                              onClick={() => handleOnClick(childNode)}
                            >
                              <img
                                src={infoIcon}
                                className="w-4 h-4 bg-blue-600 rounded-xl hover:shadow-[0_0_0_8px_rgb(59,130,246)]"
                                alt="info-icon"
                              />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
          )}
        </>
      ) : null}
    </>
  );
};

export default ServiceLeftSidebar;
